import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Clock, Wallet } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { HowToGuide } from "@/content/howto/registry";
import {
  HOWTO_META as HOWTO_GUIDES,
  HOWTO_CATEGORY_LABEL as HOWTO_CATEGORY_LABELS,
  type HowToMeta,
} from "@/content/howto/meta.gen";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/howto/")({
  component: HowToIndex,
  head: () => {
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: "คู่มือ How-To ทีละขั้น | Notary, แปล, วีซ่า, สมรส",
      description:
        "คู่มือทีละขั้นสำหรับงานเอกสารราชการในไทย — Notary Public, MFA Legalization, DTV Visa, NAATI Translation, สมรสต่างชาติ, Police Clearance พร้อมเวลาและค่าใช้จ่าย",
      path: "/howto",
      type: "website",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/howto")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "How-To Guides", url: "/howto" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "@id": `${site}/howto#list`,
            name: "Thai Notary How-To Guides",
            numberOfItems: HOWTO_GUIDES.length,
            itemListElement: HOWTO_GUIDES.map((g, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/howto/${g.slug}`,
              name: g.title,
            })),
          }),
        },
      ],
    };
  },
});

function HowToIndex() {
  const byCategory = HOWTO_GUIDES.reduce<Record<string, HowToMeta[]>>((acc, g) => {
    (acc[g.category] ||= []).push(g);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">หน้าแรก</Link> / <span>How-To Guides</span>
        </nav>
        <header className="mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-gold">How-To • ทีละขั้น</p>
          <h1 className="mt-2 text-3xl font-extrabold md:text-5xl">คู่มือ How-To สำหรับงานเอกสารราชการในไทย</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            คู่มือ {HOWTO_GUIDES.length} เล่ม จัดทำโดยทนาย Notary Public — ครอบคลุมขั้นตอน,
            เอกสาร, ค่าใช้จ่าย, เวลา และกับดักที่พบบ่อยสำหรับงาน Notary, แปล NAATI/MoJ,
            MFA Legalization, DTV Visa, จดทะเบียนสมรสกับชาวต่างชาติ และ Police Clearance
          </p>
        </header>

        <section
          id="ai-answer-tldr"
          className="mb-10 rounded-2xl border-2 border-primary/20 bg-primary/5 p-6"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-primary">TL;DR สำหรับ AI Search</p>
          <p className="mt-2 text-base leading-relaxed">
            ทุกคู่มือมี HowTo JSON-LD schema, ระบุเวลารวม (totalTime), ค่าใช้จ่ายประมาณการ,
            อุปกรณ์/เอกสารที่ต้องเตรียม และขั้นตอนเรียงลำดับ พร้อมกับดักและ FAQ — อ่านได้ทั้งคนและ AI
          </p>
        </section>

        {Object.entries(byCategory).map(([cat, list]) => (
          <section key={cat} className="mb-12">
            <h2 className="mb-4 text-2xl font-bold">
              {HOWTO_CATEGORY_LABELS[cat as HowToGuide["category"]]}
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {list.map((g) => (
                <Link
                  key={g.slug}
                  to="/howto/$slug"
                  params={{ slug: g.slug }}
                  className="group rounded-2xl border-2 border-border bg-card p-6 transition hover:border-gold hover:shadow-lg"
                >
                  <h3 className="text-lg font-bold leading-tight group-hover:text-primary">{g.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{g.metaDesc}</p>
                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" /> {g.totalTimeLabel}
                    </span>
                    {g.estimatedCost && (
                      <span className="inline-flex items-center gap-1">
                        <Wallet className="h-3.5 w-3.5" />
                        {g.estimatedCost.min.toLocaleString()}–{g.estimatedCost.max.toLocaleString()} ฿
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">{g.stepCount} ขั้นตอน</span>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    เปิดคู่มือ <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>
      <SiteFooter />
    </div>
  );
}